import {StrictMode} from "react";

import "antd/dist/reset.css";
import "./index.less";
import {Auth, UIConfigContext} from "@digatex/digatex-ui-lib";
import axios from "axios";

import {BrowserRouter} from "react-router-dom";
import {properties, UI_CONFIG} from "./properties.js";
import {IHRouter} from "./Router.jsx";
import * as serviceWorker from "./serviceWorker.js";

import {loadVariants} from "./variants/index.js";
import {App as AntdApp, ConfigProvider, message} from "antd";

import {createRoot} from "react-dom/client";
import {AntdStaticAdapter} from "@/utils/antdStaticAdapter.jsx";
import {extractErrorMessage} from "@/utils/extractErrorMessage.js";

function jiraHelpdesk(callback) {
	const jhdScript = document.createElement("script");
	jhdScript.type = "text/javascript";
	jhdScript.setAttribute("data-jsd-embedded", null);
	jhdScript.setAttribute("data-key", properties.jiraDataKey);
	jhdScript.setAttribute("data-base-url", "https://jsd-widget.atlassian.com");
	jhdScript.src = "https://jsd-widget.atlassian.com/assets/embed.js";
	if (jhdScript.readyState) {
		jhdScript.onreadystatechange = function () {
			if (jhdScript.readyState === "loaded" || jhdScript.readyState === "complete") {
				jhdScript.onreadystatechange = null;
				callback();
			}
		};
	} else {
		jhdScript.onload = function () {
			callback();
		};
	}
	document.head.appendChild(jhdScript);
}

jiraHelpdesk(function () {
	const DOMContentLoaded_event = new Event("DOMContentLoaded", {bubbles: true, cancelable: true});
	window.document.dispatchEvent(DOMContentLoaded_event);
});

axios.interceptors.request.use(config => {
	if (Auth.isAuthenticated()) {
		const cb = () => {
			config.headers = {...config.headers, ...Auth.headers(config.url)};
			return Promise.resolve(config);
		};
		return Auth.updateToken(cb);
	}
});

const recentErrorMessages = new Map();
let isLoggingOut = false;

function showErrorMessageOnce(msg) {
	const trimmed = (msg || "").trim();
	if (!trimmed) {
		message.error(msg);
		return;
	}
	const lastShown = recentErrorMessages.get(trimmed);
	if (lastShown && Date.now() - lastShown < 2500) {
		return;
	}
	message.error(trimmed);
	recentErrorMessages.set(trimmed, Date.now());
	setTimeout(() => {
		recentErrorMessages.delete(trimmed);
	}, 2500);
}

axios.interceptors.response.use(
	response => response,
	error => {
		const status = error.response?.status;
        if (status === 401) {
                        if (!isLoggingOut) {
                                isLoggingOut = true;
                                Auth.logout({ redirectUri: window.location.origin + "/" });
                        }
                        return Promise.reject(error);
                }
		if (status === 403) {
			return Promise.reject(error);
		}
		if ((status && status >= 400 && status <= 599) || !error.response) {
			if (error.config?.omitGlobalError) {
				return Promise.reject(error);
			}
			return extractErrorMessage(error).then(msg => {
				showErrorMessageOnce(msg);
				return Promise.reject(error);
			});
		}
		return Promise.reject(error);
	},
);

const Router = IHRouter;

const container = document.getElementById("root");
const root = createRoot(container);

const rootStyle = getComputedStyle(document.documentElement);
const primaryColorValue = rootStyle.getPropertyValue("--primary-color").trim();

Auth.init(() =>
	loadVariants().then(() =>
		root.render(
			<StrictMode>
				<BrowserRouter>
					<ConfigProvider
						theme={{
							token: {
								colorPrimary: primaryColorValue,
                                colorLink: primaryColorValue,
                                fontFamily: 'Calibri, sans-serif',
							},
						}}
						componentSize="small">
						<AntdApp>
							<AntdStaticAdapter />
							<UIConfigContext.Provider value={UI_CONFIG}>
								<Router />
							</UIConfigContext.Provider>
						</AntdApp>
					</ConfigProvider>
				</BrowserRouter>
			</StrictMode>,
		),
	),
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
